import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { TabWithStarIcon } from 'src/components/Listings/InventoryEventPage/TabWithStarIcon';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { IconsFill, IconsProps } from 'src/svgs/Viagogo';
import { Feature, UserSetting } from 'src/WebApiController';

import * as styles from './MenuItem.css';

type MenuItemProps = {
  isActive?: boolean;
  textContent: React.ReactNode;
  path: string;
  icon: (props: IconsProps) => JSX.Element;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  subRoutes?: Record<
    string,
    { Path: string; Icon: (props: IconsProps) => JSX.Element }
  >;
  favorite?: UserSetting;
};

export const MenuItem = ({
  isActive = false,
  textContent,
  icon: Icon,
  path,
  onClick,
  subRoutes,
  favorite,
}: MenuItemProps) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const handleContextMenu = (e: { preventDefault: () => void }) => {
    e.preventDefault(); // Prevent the default browser context menu
    setShowContextMenu((prev) => !prev);
  };

  const handleUnhover = () => {
    setShowContextMenu(false);
  };

  const isConsignorMasterView = useUserHasConsignorMasterUI();
  const hasSearchV2Feature = useUserHasFeature(Feature.AdvancedSearchV2);

  const subRoutesEnabled = {
    Search: !isConsignorMasterView,
    SearchV2: !isConsignorMasterView && hasSearchV2Feature,
  } as Record<string, boolean>;

  const {
    value: favoriteMenuTabPath,
    setUserSetting: setFavoriteMenuTabPath,
    isLoading: favoriteMenuTabPathLoading,
  } = useServerUserSetting<string>({
    id: favorite,
  });

  const onSetDefaultMenuTabClick = useCallback(
    async (tab: string) => {
      setFavoriteMenuTabPath(tab === 'none' ? '' : tab);
    },
    [setFavoriteMenuTabPath]
  );

  return (
    <Stack
      className={clsx(styles.menuItemContainer, {
        [styles.expandedMenuItemContainer]: showContextMenu,
        [styles.expandedActiveMenuItemContainer]: showContextMenu && isActive,
      })}
      onClick={onClick}
      onContextMenu={handleContextMenu}
      direction="column"
      onMouseLeave={handleUnhover}
    >
      <Link
        to={favoriteMenuTabPath ? `${path}${favoriteMenuTabPath}` : path}
        className={clsx(styles.menuItem, { [styles.activeItem]: isActive })}
      >
        <div className={styles.icon}>
          <Icon
            withHoverEffect
            fill={IconsFill.currentColor}
            isActive={isActive}
          />
        </div>
        <span className={styles.menuText}>{textContent}</span>
      </Link>
      {showContextMenu &&
        subRoutes &&
        Object.entries(subRoutes)
          .filter(([linkName]) => subRoutesEnabled[linkName])
          .map(([linkName, { Path, Icon: SubIcon }]) => (
            <Link
              key={linkName}
              to={`${path}${Path}`}
              className={clsx(styles.menuItem, {
                [styles.activeItem]: isActive,
              })}
            >
              <div className={styles.icon}>
                <SubIcon withHoverEffect fill={IconsFill.currentColor} />
              </div>
              <TabWithStarIcon
                onIconClick={onSetDefaultMenuTabClick}
                defaultTab={favoriteMenuTabPath}
                tabType={Path}
              >
                <span className={styles.menuText}>{linkName}</span>
              </TabWithStarIcon>
            </Link>
          ))}
    </Stack>
  );
};
